import React, { useState, useRef, useEffect, useCallback } from 'react';
import { SendOutlined, RobotOutlined } from '@ant-design/icons';
import '../assets/css/home.css';
import { apiPostMessage, apiLawerInfo, apiGetHistoryMessage } from '../request/api';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { zeusApp } from '../request/index';

interface HistoryMessage {
  id: string;
  dialoguePcId: string;
  isAnswer: number; // 0:用户, 1:AI
  state: string;
  type: string;
  content: string;
  file: any;
  fileSource: any;
  duration: any;
  sendAt: string;
}

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'ai';
  timestamp: Date;
  type?: string;
  uscc?: string;
  userId?: string;
  appId?: string;
  token?: string;
}

const PAGE_SIZE = 15;

const Home: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputText, setInputText] = useState('');
  const [lawyerucc, setLawyerucc] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isChatStarted, setIsChatStarted] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [hasMoreHistory, setHasMoreHistory] = useState(true);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isPageLoading, setIsPageLoading] = useState(true);

  const hasInitRef = useRef(false);
  const isLoadingInitRef = useRef(false);
  const userId = localStorage.getItem('id') || '';
  const userName = localStorage.getItem('userName') || '';
  const token = localStorage.getItem('userToken') || '';
  const appId = zeusApp || '';
  const [connectionStatus, setConnectionStatus] = useState('未连接');

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const initialInputRef = useRef<HTMLTextAreaElement>(null);
  const chatInputRef = useRef<HTMLTextAreaElement>(null);
  const socketRef = useRef<WebSocket | null>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const isLoadingMoreRef = useRef(false);
  const shouldScrollToBottomRef = useRef(false);
  const typingTimersRef = useRef<Map<string, NodeJS.Timeout>>(new Map());
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lawyeruccRef = useRef('');

  const getToken = () => {
    return localStorage.getItem('userToken') || '';
  };

  useEffect(() => {
    if (!lawyerucc) return;
    loadHistoryMessages(1, false)
  }, [lawyerucc]);

  const scrollToBottom = useCallback(() => {
    if (shouldScrollToBottomRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  const typeWriter = useCallback((fullText: string, messageId: string, speed = 30) => {
    let index = 0;

    const timer = setInterval(() => {
      index++;

      setMessages(prev =>
        prev.map(msg =>
          msg.id === messageId
            ? { ...msg, text: fullText.slice(0, index) }
            : msg
        )
      );

      if (index >= fullText.length) {
        clearInterval(timer);
        typingTimersRef.current.delete(messageId);
        setIsLoading(false);

        shouldScrollToBottomRef.current = true;
        setTimeout(() => {
          scrollToBottom();
        }, 100);
      }
    }, speed);

    typingTimersRef.current.set(messageId, timer);
  }, [scrollToBottom]);

  const loadHistoryMessages = useCallback(async (page: number = 1, isLoadMore: boolean = false) => {
    console.log(lawyerucc)
    console.log(isLoadingMoreRef.current)
    if (!lawyerucc || isLoadingMoreRef.current) return;

    setIsLoadingHistory(true);
    isLoadingMoreRef.current = true;

    try {
      const historyInfo = {
        uscc: lawyerucc,
        page: page,
        size: PAGE_SIZE
      };

      const res = await apiGetHistoryMessage(historyInfo);

      if (res.data.ret === '0') {
        const historyData = res.data.data;
        setHistoryTotal(historyData.total);

        const loadedCount = page * PAGE_SIZE;
        const hasMore = loadedCount < historyData.total;
        setHasMoreHistory(hasMore);

        const newHistoryMessages: Message[] = historyData.rows.map((item: HistoryMessage) => ({
          id: item.id,
          text: item.content,
          sender: item.isAnswer === 1 ? 'ai' : 'user',
          timestamp: new Date(item.sendAt),
          type: '1',
          uscc: lawyerucc,
          userId: item.isAnswer === 1 ? undefined : userId
        }));

        const sortedMessages = newHistoryMessages.sort((a, b) =>
          a.timestamp.getTime() - b.timestamp.getTime()
        );

        if (page === 1) {
          setMessages(sortedMessages);
          setIsChatStarted(true);

          if (isInitialLoad && !isLoadMore) {
            shouldScrollToBottomRef.current = true;
            setTimeout(() => {
              scrollToBottom();
              setIsInitialLoad(false);
            }, 300);
          }
        } else {
          setMessages(prev => [...sortedMessages, ...prev]);
          shouldScrollToBottomRef.current = false;
        }

        setHistoryPage(page);
      }
    } catch (error) {
      console.error('加载历史记录失败:', error);
    } finally {
      setIsLoadingHistory(false);
      isLoadingMoreRef.current = false;
    }
  }, [lawyerucc, userId, isInitialLoad, scrollToBottom]);

  const connectWebSocket = useCallback(() => {
    if (socketRef.current &&
      (socketRef.current.readyState === WebSocket.CONNECTING ||
        socketRef.current.readyState === WebSocket.OPEN)) {
      console.log('WebSocket 已存在，跳过重复连接');
      return;
    }

    const token = getToken();
    if (!token) {
      console.error('用户未登录，无法连接WebSocket');
      setConnectionStatus('未登录，请先登录');
      return;
    }

    const baseUrl = 'wss://api.zeus.ipcn.cn/ws/chat';
    const wsUrl = `${baseUrl}?zeus-token=${encodeURIComponent(token)}&zeus-app=${encodeURIComponent(zeusApp)}`;

    console.log('正在连接WebSocket:', wsUrl);

    try {
      const socket = new WebSocket(wsUrl);
      socketRef.current = socket;

      socket.onopen = () => {
        console.log('WebSocket连接已建立');
        setIsConnected(true);
        setConnectionStatus('已连接');
      };

      socket.onmessage = (event) => {
        try {
          const messageData = JSON.parse(event.data);
          handleWebSocketMessage(messageData);
        } catch (error) {
          console.error('解析WebSocket消息错误:', error);
        }
      };

      socket.onclose = (event) => {
        console.log('WebSocket连接已关闭', event.code, event.reason);
        setIsConnected(false);
        setConnectionStatus('已断开');

        setTimeout(() => {
          console.log('尝试重新连接...');
          connectWebSocket();
        }, 5000);
      };

      socket.onerror = (error) => {
        console.error('WebSocket错误:', error);
        setConnectionStatus('连接错误');
        setIsConnected(false);
      };

    } catch (error) {
      console.error('创建WebSocket连接失败:', error);
      setConnectionStatus('连接失败');
    }
  }, []);

  const loadMoreHistory = useCallback(async () => {
    if (isLoadingMoreRef.current || !hasMoreHistory) return;

    const nextPage = historyPage + 1;
    await loadHistoryMessages(nextPage, true);
  }, [historyPage, hasMoreHistory, loadHistoryMessages]);

  useEffect(() => {
    const init = async () => {
      if (isLoadingInitRef.current || hasInitRef.current) {
        return;
      }

      isLoadingInitRef.current = true;
      console.log('开始初始化...');

      try {
        const res = await apiLawerInfo();
        console.log('律师信息响应:', res);

        if (res.data.ret === '0') {
          const uscc = res.data.data.uscc;
          console.log('律师信息 uscc:', uscc);
          setLawyerucc(uscc);

        } else {
          console.error('获取律师信息失败:', res.data.msg);
        }
      } catch (error) {
        console.error('初始化失败:', error);
      } finally {
        setIsPageLoading(false);
        isLoadingInitRef.current = false;
        hasInitRef.current = true;
      }
    };

    if (!hasInitRef.current) {
      connectWebSocket();
      init();
    }

    return () => {
      console.log('组件卸载清理');
      if (socketRef.current) {
        socketRef.current.close();
      }

      typingTimersRef.current.forEach((timer) => {
        clearInterval(timer);
      });

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []); // 空依赖数组确保只执行一次

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        const isAtTop = container.scrollTop <= 10;
        const hasScrollContent = container.scrollHeight > container.clientHeight;

        if (isAtTop && hasScrollContent && hasMoreHistory && !isLoadingHistory) {
          loadMoreHistory();
        }
      }, 200);
    };

    container.addEventListener('scroll', handleScroll);
    return () => {
      container.removeEventListener('scroll', handleScroll);
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [hasMoreHistory, isLoadingHistory, loadMoreHistory]);

  useEffect(() => {
    if (shouldScrollToBottomRef.current) {
      scrollToBottom();
    }
  }, [messages, scrollToBottom]);

  const handleWebSocketMessage = (messageData: any) => {
    console.log('收到WebSocket消息:', messageData);

    if (messageData.type === 'AI' || messageData.sender === 'AI') {
      const fullText = messageData.content || messageData.text || '';
      const messageId = `ai_${Date.now()}`;

      setIsLoading(false);

      const aiMessage: Message = {
        id: messageId,
        text: '',
        sender: 'ai',
        timestamp: new Date(),
        type: messageData.type || 'AI',
        uscc: lawyerucc
      };

      setMessages(prev => [...prev, aiMessage]);

      requestAnimationFrame(() => {
        typeWriter(fullText, messageId, 25);
      });

      return;
    }

    if (messageData.type === 'SYSTEM') {
      const systemMessage: Message = {
        id: `system_${Date.now()}`,
        text: messageData.content || messageData.text || '',
        sender: 'ai',
        timestamp: new Date(),
        type: 'SYSTEM'
      };

      setMessages(prev => [...prev, systemMessage]);

      shouldScrollToBottomRef.current = true;
      setTimeout(() => {
        scrollToBottom();
      }, 100);

      return;
    }

    if (messageData.type === 'ERROR') {
      setIsLoading(false);

      const errorMessage: Message = {
        id: `error_${Date.now()}`,
        text: `系统错误：${messageData.content || messageData.text || '未知错误'}`,
        sender: 'ai',
        timestamp: new Date(),
        type: 'SYSTEM'
      };

      setMessages(prev => [...prev, errorMessage]);

      shouldScrollToBottomRef.current = true;
      setTimeout(() => {
        scrollToBottom();
      }, 100);
    }
  };

  const sendWebSocketMessage = (content: string) => {
    if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) {
      console.error('WebSocket未连接');
      setConnectionStatus('发送失败：未连接');
      return false;
    }

    const message = {
      id: `msg_${Date.now()}`,
      content: content,
      sender: 'user',
      type: 'USER',
      timestamp: Date.now(),
      uscc: lawyerucc,
      userId: userId + '.' + userName,
      token: token,
      appId: appId
    };

    try {
      socketRef.current.send(JSON.stringify(message));
      console.log('已发送消息:', message);
      return true;
    } catch (error) {
      console.error('发送WebSocket消息失败:', error);
      return false;
    }
  };

  useEffect(() => {
    if (!isChatStarted && initialInputRef.current) {
      initialInputRef.current.focus();
    } else if (isChatStarted && chatInputRef.current) {
      chatInputRef.current.focus();
    }
  }, [isChatStarted]);

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputText(e.target.value);

    const textarea = e.target;
    textarea.style.height = 'auto';
    const scrollHeight = textarea.scrollHeight;
    const lineHeight = 24;
    const padding = 32;
    const lines = Math.max(textarea.value.split('\n').length, 1);
    const calculatedHeight = Math.max(lines * lineHeight + padding, 48);
    const newHeight = Math.min(Math.max(scrollHeight, calculatedHeight), 400);

    textarea.style.height = newHeight + 'px';

    if (newHeight >= 400) {
      textarea.style.overflowY = 'auto';
    } else {
      textarea.style.overflowY = 'hidden';
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();

      const canSend = inputText.trim() && !isLoading && isConnected;

      if (canSend) {
        handleSendMessage();
      }
    }
  };

  // 修复后的时间格式化函数
  const formatTime = (date: Date) => {
    const now = new Date();
    const messageDate = new Date(date);

    // 清除时间部分，只比较日期
    const today = new Date(now);
    today.setHours(0, 0, 0, 0);

    const messageDay = new Date(messageDate);
    messageDay.setHours(0, 0, 0, 0);

    const diffDays = Math.round((today.getTime() - messageDay.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      // 今天：14:30
      return messageDate.toLocaleTimeString('zh-CN', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
    } else if (diffDays === 1) {
      // 昨天：昨天 14:30
      const time = messageDate.toLocaleTimeString('zh-CN', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
      return `昨天 ${time}`;
    } else if (diffDays === 2) {
      // 前天：前天 14:30
      const time = messageDate.toLocaleTimeString('zh-CN', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
      return `前天 ${time}`;
    } else if (diffDays < 7) {
      // 一周内：星期X 14:30
      const weekdays = ['日', '一', '二', '三', '四', '五', '六'];
      const weekday = weekdays[messageDate.getDay()];
      const time = messageDate.toLocaleTimeString('zh-CN', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
      return `周${weekday} ${time}`;
    } else if (messageDate.getFullYear() === now.getFullYear()) {
      // 今年其他时间：04-20 14:30
      const month = String(messageDate.getMonth() + 1).padStart(2, '0');
      const day = String(messageDate.getDate()).padStart(2, '0');
      const time = messageDate.toLocaleTimeString('zh-CN', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
      return `${month}-${day} ${time}`;
    } else {
      // 往年：2023-04-20 14:30
      const year = messageDate.getFullYear();
      const month = String(messageDate.getMonth() + 1).padStart(2, '0');
      const day = String(messageDate.getDate()).padStart(2, '0');
      const time = messageDate.toLocaleTimeString('zh-CN', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
      return `${year}-${month}-${day} ${time}`;
    }
  };

  const handleSendMessage = async () => {
    if (!inputText.trim() || isLoading || !isConnected) return;

    const userMessage = inputText.trim();
    setInputText('');

    if (initialInputRef.current) {
      initialInputRef.current.style.height = 'auto';
    }
    if (chatInputRef.current) {
      chatInputRef.current.style.height = 'auto';
    }

    if (!isChatStarted) {
      setIsChatStarted(true);
    }

    const newUserMessage: Message = {
      id: `user_${Date.now()}`,
      text: userMessage,
      sender: 'user',
      timestamp: new Date(),
      uscc: lawyerucc,
      userId: userId
    };

    const updatedMessages = [...messages, newUserMessage];
    setMessages(updatedMessages);

    setIsLoading(true);

    shouldScrollToBottomRef.current = true;
    setTimeout(() => {
      scrollToBottom();
    }, 50);

    let sendSuccess = false;
    if (isConnected) {
      sendSuccess = sendWebSocketMessage(userMessage);
    } else {
      const token = getToken();
      if (token) {
        connectWebSocket();
        setTimeout(() => {
          if (isConnected) {
            sendSuccess = sendWebSocketMessage(userMessage);
          } else {
            setConnectionStatus('发送失败：未连接');
            setIsLoading(false);
          }
        }, 1000);
      } else {
        setConnectionStatus('未登录，请先登录');
        setIsLoading(false);
      }
    }

    if (!sendSuccess) {
      console.log('WebSocket发送失败，尝试HTTP API');
    }
  };

  const clearChat = () => {
    if (window.confirm('确定要清空所有对话吗？')) {
      typingTimersRef.current.forEach((timer) => {
        clearInterval(timer);
      });
      typingTimersRef.current.clear();

      setMessages([]);
      if (messages.length > 0) {
        const systemMessage: Message = {
          id: `system_${Date.now()}`,
          text: '对话已清空，请开始新的对话！',
          sender: 'ai',
          timestamp: new Date(),
          type: 'SYSTEM',
          uscc: lawyerucc
        };
        setMessages([systemMessage]);

        shouldScrollToBottomRef.current = true;
        setTimeout(() => {
          scrollToBottom();
        }, 100);
      }
    }
  };

  const reconnectWebSocket = () => {
    const token = getToken();
    if (token) {
      if (socketRef.current) {
        socketRef.current.close();
      }
      setConnectionStatus('重新连接中...');
      connectWebSocket();
    } else {
      setConnectionStatus('未登录，请先登录');
    }
  };

  if (isPageLoading) {
    return (
      <div className="initial-container" style={{ justifyContent: 'center' }}>
        <div className="loading-indicator" style={{ marginBottom: '20px' }}>
          <span className="dot"></span>
          <span className="dot"></span>
          <span className="dot"></span>
        </div>
        <div style={{ color: '#666', fontSize: '14px' }}>
          正在加载中...
        </div>
      </div>
    );
  }

  if (!isChatStarted && messages.length === 0) {
    const canSend = inputText.trim() && !isLoading && isConnected;

    return (
      <div className="initial-container">
        <div className="initial-header">
          <h1><span>您好，{localStorage.getItem('userName') || '用户'}</span></h1>
        </div>
        <div className="initial-input-container">
          <textarea
            ref={initialInputRef}
            value={inputText}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder={!isConnected ? "连接中，请稍后..." : "输入消息..."}
            className="initial-chat-input"
            disabled={!isConnected}
            style={{
              cursor: isConnected ? 'text' : 'not-allowed',
              opacity: isConnected ? 1 : 0.6
            }}
          />
          {inputText.trim() && (
            <button
              onClick={handleSendMessage}
              disabled={!canSend}
              className="initial-send-button"
              style={{
                cursor: canSend ? 'pointer' : 'not-allowed',
                opacity: canSend ? 1 : 0.5
              }}
            >
              <SendOutlined />
            </button>
          )}
        </div>
        <div className="initial-input-hint">
          {isConnected ?
            "按 Enter 发送，Shift + Enter 换行 ｜ NEXTOOK 也可能会犯错，请仔细检查。" :
            "正在连接服务器，请稍后..."
          }
        </div>
        <div className="connection-status" style={{
          fontSize: '12px',
          color: isConnected ? '#10a37f' : '#ff4d4f',
          marginTop: '10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: isConnected ? '#52c41a' : '#ff4d4f',
            marginRight: '6px'
          }} />
          <span>连接状态: {connectionStatus}</span>
        </div>
      </div>
    );
  }
  

  return (
    <div className="chat-container">
      <div className="chat-header">
        <div className="chat-title">
          <RobotOutlined style={{ color: '#10a37f', marginRight: '10px' }} />
          <h1>AI助手</h1>
          <div className="connection-indicator" style={{
            marginLeft: '20px',
            fontSize: '12px',
            display: 'flex',
            alignItems: 'center'
          }}>
            <div style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: isConnected ? '#52c41a' : '#ff4d4f',
              marginRight: '5px'
            }} />
            <span>{connectionStatus}</span>
          </div>
        </div>
        <div className="chat-subtitle">
          我是你的智能助手，随时为你提供帮助
          <button
            onClick={clearChat}
            style={{
              marginLeft: '20px',
              fontSize: '12px',
              background: 'transparent',
              border: '1px solid #ddd',
              borderRadius: '4px',
              padding: '2px 8px',
              cursor: 'pointer'
            }}
          >
            清空对话
          </button>
          {historyTotal > 0 && (
            <span style={{ marginLeft: '20px', fontSize: '12px', color: '#666' }}>
              共 {historyTotal} 条历史记录
            </span>
          )}
        </div>
      </div>

      <div className="messages-container" ref={messagesContainerRef}>
        <div className="messages-list">
          {isLoadingHistory && historyPage === 1 ? (
            <div className="loading-history">
              <div className="loading-indicator">
                <span className="dot"></span>
                <span className="dot"></span>
                <span className="dot"></span>
              </div>
              <span>正在加载历史记录...</span>
            </div>
          ) : (
            <>
              {isLoadingHistory && historyPage > 1 && (
                <div className="loading-more-history">
                  <div className="loading-indicator">
                    <span className="dot"></span>
                    <span className="dot"></span>
                    <span className="dot"></span>
                  </div>
                  <span>正在加载更多历史记录...</span>
                </div>
              )}

              {messages.length === 0 ? (
                <div className="empty-state">
                  <RobotOutlined style={{ fontSize: '48px', color: '#10a37f', marginBottom: '20px' }} />
                  <h3>开始对话</h3>
                  <p>输入消息开始与AI助手对话</p>
                </div>
              ) : (
                <>
                  {messages.map((message) => (
                    <div
                      key={message.id}
                      className={`message-item ${message.sender === 'user' ? 'user-message' : 'ai-message'}`}
                    >
                      <div className="message-content">
                        {message.type === 'SYSTEM' ? (
                          <div className="system-message" style={{
                            color: '#666',
                            fontStyle: 'italic',
                            backgroundColor: '#f5f5f5',
                            borderRadius: '8px',
                            padding: '8px 12px',
                            borderLeft: '3px solid #10a37f'
                          }}>
                            <strong>系统提示：</strong>{message.text}
                          </div>
                        ) : (
                          <>
                            <div className="message-text">
                              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                {message.text}
                              </ReactMarkdown>
                            </div>
                            <div className="message-time">
                              {/* 这里已经使用修复后的 formatTime 函数 */}
                              {formatTime(message.timestamp)}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </>
              )}
            </>
          )}

          {isLoading && (
            <div className="message-item ai-message">
              <div className="message-content">
                <div className="loading-indicator">
                  <span className="dot"></span>
                  <span className="dot"></span>
                  <span className="dot"></span>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      <div className="input-container">
        <div className="input-wrapper">
          <textarea
            ref={chatInputRef}
            value={inputText}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder={!isConnected ? "连接中，请稍后..." : "输入消息..."}
            className="chat-input"
            disabled={!isConnected}
            style={{
              cursor: isConnected ? 'text' : 'not-allowed',
              opacity: isConnected ? 1 : 0.6
            }}
          />
          {inputText.trim() && (
            <button
              onClick={handleSendMessage}
              disabled={!isConnected || isLoading}
              className={`send-button ${inputText.trim() && !isLoading && isConnected ? 'active' : ''}`}
              style={{
                cursor: (isConnected && !isLoading) ? 'pointer' : 'not-allowed',
                opacity: (isConnected && !isLoading) ? 1 : 0.5
              }}
            >
              <SendOutlined />
            </button>
          )}
        </div>
        <div className="input-hint" style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <span>
            {isConnected ?
              "按 Enter 发送，Shift + Enter 换行 ｜ NEXTOOK 也可能会犯错，请仔细检查。" :
              "正在连接服务器，请稍后..."
            }
          </span>
        </div>
      </div>
    </div>
  );
};

export default Home;